/**
 * AgentManifestV1 — the ONE serialized carrier for org-published AgentProfiles.
 *
 * Used on both sides of the org lifecycle:
 *   - pending submission body   (L3  pending-agents/<scope>/…/<submitter>__<slug>.json)
 *   - published pool entry      (L5  org-agents/<scope>/<slug>/agent.json)
 *
 * CLAUDE.md §9 single source of truth: every producer MUST go through
 * {@link buildAgentManifest} (no hand-rolled object literals) and every consumer
 * through {@link parseAgentManifest} (defensive: malformed ⇒ null, never a
 * throw — the caller maps null to a friendly error / skips the entry).
 * {@link manifestToProfileInput} is the one manifest→store mapping (roles carried
 * verbatim, origin pinned to "org").
 *
 * VISIBILITY MODEL (选 A — one file per agent, roles carried in the manifest):
 * `roles` is the allowlist of department role templateIds this agent serves —
 * `[]`/absent = everyone (company-wide), `[a, b, c]` = only those roles. Multi-
 * role visibility rides on this ONE array field under ONE stable id, so it can
 * never collapse under a shared id (the pre-选A per-role path fan-out did:
 * N copies, same id, INSERT OR REPLACE kept only the last). `origin` is still
 * DERIVED at ingest (from scope: user-share ⇒ "user", else "org").
 */
import { buildMigratedCapabilities, coerceSessionProfile, MINI_AGENT_BASELINE } from "./skill-agent.js";
import { deriveSlugFromDisplayName } from "./pending-submission-path.js";
import { toCanonicalAgent } from "./agent-canonical.js";
import { canonicalJsonDigest } from "./canonical-digest.js";
function cleanStringList(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    for (const v of raw) {
        if (typeof v !== "string")
            continue;
        const t = v.trim();
        if (t && !out.includes(t))
            out.push(t);
    }
    return out;
}
function optionalString(raw) {
    return typeof raw === "string" && raw.trim() ? raw : undefined;
}
/**
 * Derive the stable publish slug for an Agent from its display name — the ONE
 * slug rule shared by the server normalizer (authoritative) and any client
 * display. Deterministic: the same name always yields the same slug, so a
 * re-submission of an updated Agent maps onto the same published id
 * (`agent_org_<slug>`) and re-publish UPDATES instead of duplicating.
 *
 * - Pure-ASCII names slugify readably ("Brief Agent" → "brief-agent").
 * - Any non-ASCII (CJK) name goes to a stable FNV-1a hash (`agent-<hex8>`) —
 *   NEVER the stripped-ASCII残余 ("会议纪要 Agent" must not collapse to
 *   "agent" and collide with every other「XX Agent」).
 */
export function deriveAgentSlug(name) {
    // Thin wrapper over the ONE display-name slug rule (pending-submission-path.ts)
    // — the pending cloud-task queue derives its own slug from the same core with a
    // `task-` prefix, so the ASCII/FNV grammar can never fork (红线 #9).
    return deriveSlugFromDisplayName(name, "agent");
}
/** The ONE manifest builder. Throws with an actionable message on invalid input
 *  (producers are trusted code paths — a throw here is a programming error). */
export function buildAgentManifest(args) {
    if (!args.id.trim())
        throw new Error("AgentManifest: id is required");
    if (!args.profile.name?.trim())
        throw new Error("AgentManifest: profile.name is required");
    const roles = cleanStringList(args.roles);
    if (args.scope === "department" && roles.length === 0) {
        throw new Error("AgentManifest: department scope requires at least one role");
    }
    if (args.scope === "user" && !args.recipientUserId?.trim()) {
        throw new Error("AgentManifest: user scope requires recipientUserId");
    }
    if (!args.submitter.trim())
        throw new Error("AgentManifest: submitter is required");
    const p = args.profile;
    const capabilities = coerceSessionProfile(p.capabilities);
    const name = p.name.trim();
    return {
        v: 1,
        id: args.id.trim(),
        profile: {
            name: p.name.trim(),
            ...(optionalString(p.description) ? { description: p.description } : {}),
            ...(optionalString(p.systemPrompt) ? { systemPrompt: p.systemPrompt } : {}),
            // 能力面：走唯一整形器 coerceSessionProfile（红线 #9，与桌面 sqlite 列同一个）。
            // 未声明 ⇒ 字段省略；接收端 ingest 后由载体落 MINI_AGENT_BASELINE。
            ...(capabilities ? { capabilities } : {}),
            ...(optionalString(p.model) ? { model: p.model } : {}),
            ...(optionalString(p.icon) ? { icon: p.icon } : {}),
        },
        scope: args.scope,
        // roles serialized only for department scope (non-empty, validated above);
        // company/user omit it — absent ⇒ everyone (company) / recipient-only (user).
        ...(args.scope === "department" ? { roles } : {}),
        ...(args.scope === "user" ? { recipientUserId: args.recipientUserId.trim() } : {}),
        submitter: args.submitter.trim(),
        ...(args.publishedAt ? { publishedAt: args.publishedAt } : {}),
        // 派生一次，写在这里；提交方送来的 canonical 一律忽略（见字段注释）。
        canonical: toCanonicalAgent({
            id: args.id.trim(),
            name,
            ...(optionalString(p.description) ? { description: p.description } : {}),
            ...(capabilities ? { capabilities } : {}),
        }),
    };
}
/**
 * 「发布即快照」的快照摘要（N-83 P3-01，proposal 增量 9）——只覆盖 `id` 与 `profile`
 * （含能力面），**不含** `publishedAt` / `scope` / `roles` / `submitter` / `canonical`：
 * 这些字段要么每次发布都变（`publishedAt`）、要么不影响飞书机器人回答什么（`scope`/
 * `roles`/`submitter` 是可见范围与审计元数据）、要么是从 `profile` 派生出来的
 * （`canonical`，与摘要要判断的「配置变没变」同一件事的复述，纳入会让摘要对同一份
 * `profile` 因为派生实现变化而跟着变）。用同一个 {@link canonicalJsonDigest}，
 * 不另写一份摘要算法（红线 #9）。
 */
export function agentSnapshotDigest(m) {
    return canonicalJsonDigest({ id: m.id, profile: m.profile });
}
/**
 * 发布快照锁定的模型（N-182 P1 R1.5 / R1.6；飞书机器人本地执行与 agent-host 对话轮次读同一份）：写了具体模型 id →
 * 锁定模型（原样交给飞书选模型，`[pi]` 由它判引擎后去掉）；空串 / `inherit` → 没锁、跟随主脑。
 */
export function agentManifestLockedModel(m) {
    const model = (m.profile.model ?? "").trim();
    return model && model !== "inherit" ? model : undefined;
}
/** The ONE manifest parser — defensive deserialization boundary (§9): guards
 *  the parent object before every index, malformed ⇒ null, never throws. */
export function parseAgentManifest(raw) {
    let data;
    try {
        data = JSON.parse(raw);
    }
    catch {
        return null;
    }
    if (!data || typeof data !== "object" || Array.isArray(data))
        return null;
    const m = data;
    if (m.v !== 1)
        return null;
    if (typeof m.id !== "string" || !m.id.trim())
        return null;
    const rawProfile = m.profile;
    if (!rawProfile || typeof rawProfile !== "object" || Array.isArray(rawProfile))
        return null;
    const p = rawProfile;
    if (typeof p.name !== "string" || !p.name.trim())
        return null;
    const scope = m.scope;
    if (scope !== "company" && scope !== "department" && scope !== "user")
        return null;
    // roles = the new array carrier; LEGACY compat (§8 cross-version): an old
    // manifest carried a single `roleSlug` — read it as `[roleSlug]` when `roles`
    // is absent, so catalyst prod's existing 旧格式 pool entries parse instead of
    // crashing. Both absent ⇒ [] (everyone).
    const rolesRaw = cleanStringList(m.roles);
    const legacyRole = optionalString(m.roleSlug);
    const roles = rolesRaw.length ? rolesRaw : legacyRole ? [legacyRole] : [];
    if (scope === "department" && roles.length === 0)
        return null;
    const recipientUserId = optionalString(m.recipientUserId);
    if (scope === "user" && !recipientUserId)
        return null;
    if (typeof m.submitter !== "string" || !m.submitter.trim())
        return null;
    const capabilities = coerceSessionProfile(p.capabilities);
    const built = buildAgentManifest({
        id: m.id,
        profile: {
            name: p.name,
            description: optionalString(p.description),
            systemPrompt: optionalString(p.systemPrompt),
            capabilities,
            model: optionalString(p.model),
            icon: optionalString(p.icon),
        },
        scope,
        ...(scope === "department" ? { roles } : {}),
        ...(scope === "user" ? { recipientUserId } : {}),
        submitter: m.submitter,
        ...(optionalString(m.publishedAt) ? { publishedAt: m.publishedAt } : {}),
    });
    // LEGACY 能力面 compat（§8 跨版本数据兼容，见 AgentManifestV1.legacy）：#1648 之前
    // 的池条目把能力面写在 `profile.tools` / `profile.skills`。只在**没有**新字段时接住
    // ——有 capabilities 就以它为准，旧字段是同一份信息的过期副本，读它只会打架。
    // 附加在 buildAgentManifest 之后而不是传进去，是为了让「唯一构建器」保持只产新格式：
    // 任何一次重新发布都自然把旧字段甩掉，不会把它写回池里。
    if (!capabilities) {
        const legacyTools = cleanStringList(p.tools);
        const legacySkills = cleanStringList(p.skills);
        if (legacyTools.length || legacySkills.length) {
            built.legacy = { tools: legacyTools, skills: legacySkills };
        }
    }
    return built;
}
/** The ONE manifest→store mapping: `roles` carries verbatim, scope drives origin.
 *  - roles []/absent ⇒ everyone (company);  [a,b,c] ⇒ those roles (department).
 *  - origin: user-scope share ⇒ "user" (recipient's OWN editable workbench agent,
 *    lands only on their machine); every org publish ⇒ "org" (admin-managed,
 *    locally read-only).
 *
 *  【能力面归一 —— 这是本函数存在的第二个理由】旧池条目（#1648 之前发布的）把能力面
 *  写在 `profile.tools`/`profile.skills`，解析器把它们接在 `m.legacy`。这里是
 *  manifest→store 的**唯一**边界，故一次性把它们跑成 `capabilities` 就地归一，跑的是
 *  与本机存量迁移**同一个** {@link buildMigratedCapabilities}（禁第二份实现，红线 #9）
 *  —— 同一份旧字段，不论走「本机建的」还是「管理员发的」，迁出的能力面必须一致。
 *
 *  不归一的后果不是「少点东西」而是空壳：无 capabilities → 载体落 MINI_AGENT_BASELINE
 *  → 管理员发给全公司的业务 Agent 只剩只读底座，且 org 行本地只读、无人能修。
 *
 *  @param dosiaToolNames dosia-tools 注册名全集，**必填**。做成必填而不是可选是刻意的：
 *  可选 = 某个调用点漏传就静默把所有 dosia 工具当噪音丢弃（旧条目照样空壳），正是那种
 *  「下发到位、生效失守」的第二道门失守。必填让 TS 在每个接线点兜底。桌面侧统一从
 *  `describeMigrationDosiaToolNames()` 取（与本机迁移同一个取数口）。 */
export function manifestToProfileInput(m, dosiaToolNames) {
    const origin = m.scope === "user" ? "user" : "org";
    const capabilities = m.profile.capabilities
        ?? (m.legacy ? buildMigratedCapabilities(m.legacy, new Set(dosiaToolNames)) : undefined);
    return {
        id: m.id,
        input: {
            ...m.profile,
            ...(capabilities ? { capabilities } : {}),
            roles: m.roles ?? [],
            origin,
            // Carry the manifest's real publish time so ingestOrgAgents can seed a
            // first-ingest created_at from it (workbench「本周新上线」dating).
            publishedAt: m.publishedAt,
        },
    };
}
/**
 * 把一份**已解析**的池 manifest 折成 {@link PublishedOrgAgentSummary} —— 列表端点的唯一
 * 换算点。能力面归一走与 {@link manifestToProfileInput} **同一条**逻辑（`capabilities` 缺失
 * 时读 legacy → `buildMigratedCapabilities`，都没有则落 {@link MINI_AGENT_BASELINE} 五轴全空），
 * 保证「员工机器 ingest 出来的」与「管理员在后台看到的」是同一份能力面，禁第二份换算。
 */
export function summarizePublishedOrgAgent(slug, m, dosiaToolNames) {
    const capabilities = m.profile.capabilities ??
        (m.legacy ? buildMigratedCapabilities(m.legacy, new Set(dosiaToolNames)) : { ...MINI_AGENT_BASELINE });
    return {
        slug,
        name: m.profile.name,
        ...(m.profile.description ? { description: m.profile.description } : {}),
        ...(m.profile.systemPrompt ? { systemPrompt: m.profile.systemPrompt } : {}),
        ...(m.profile.model ? { model: m.profile.model } : {}),
        ...(m.profile.icon ? { icon: m.profile.icon } : {}),
        // user scope never reaches the pool (buildPublishedPath only writes company/department);
        // treat any non-department as company for the admin view.
        targetScope: m.scope === "department" ? "department" : "company",
        roles: m.scope === "department" ? m.roles ?? [] : [],
        ...(m.publishedAt ? { publishedAt: m.publishedAt } : {}),
        capabilities,
        // 从**归一后**的 capabilities 现算，而不是读 m.canonical：legacy 池条目的
        // m.canonical 是从空能力面派生的（解析时 capabilities 还没归一），直接读会
        // 让后台把「#1648 之前发布的业务 Agent」显示成零依赖。
        canonical: toCanonicalAgent({
            id: m.id,
            name: m.profile.name,
            ...(m.profile.description ? { description: m.profile.description } : {}),
            capabilities,
        }),
    };
}
//# sourceMappingURL=agent-manifest.js.map